/* External */
import jwtDecode from 'jwt-decode';

/* Ott-common */
import { getUserTypeByBusinessUnit } from '@dtvgo/utils';
import { LOCATIONS_ERROR } from '@dtvgo/code-error';

/* Utils */
import platform from './platform';
import Subscription from './Subscription';
import { getLink, Routes } from './router';
import { redirectTo } from './history';

const accessTokenDataSubscription = Subscription();
export const addAccessTokenDataSubscriber = accessTokenDataSubscription.addSubscriber;
export const removeAccessTokenDataSubscriber = accessTokenDataSubscription.removeSubscriber;

const ACCESS_TOKEN_DATA_KEY = 'accessTokenData';

const callTracker = {
  lastCallTime: 0,
  callCount: 0,
  timeWindow: 3000,
};

function checkDuplicateCall() {
  const now = Date.now();

  if (now - callTracker.lastCallTime < callTracker.timeWindow) {
    callTracker.callCount += 1;
    if (callTracker.callCount >= 1) {
      onSessionError();
    }
  } else {
    callTracker.callCount = 1;
  }

  callTracker.lastCallTime = now;
}

const SELECT_PROFILE = 'profile';
const SELECT_PROFILES = 'profiles';

function saveAccessTokenData(newAccessTokenData) {
  localStorage.setItem(ACCESS_TOKEN_DATA_KEY, JSON.stringify(newAccessTokenData));
  platform.saveCredentials(ACCESS_TOKEN_DATA_KEY, JSON.stringify(newAccessTokenData));
}

export function setAccessTokenData(newAccessTokenData) {
  if (!newAccessTokenData) {
    removeAccessTokenData();
    return;
  }

  saveAccessTokenData(newAccessTokenData);
  accessTokenDataSubscription.notifySubscribers(newAccessTokenData);
  checkDuplicateCall();
}

export async function getAccessTokenData() {
  try {
    let accessTokenData = getFromLocalStorage(ACCESS_TOKEN_DATA_KEY);

    if (!accessTokenData) {
      accessTokenData = await getFromPlatformStorage(ACCESS_TOKEN_DATA_KEY);
    }

    if (!accessTokenData) {
      accessTokenData = await platform.getAccessTokenAlternative();

      if (accessTokenData) {
        platform.removeAccessTokenAlternative();
        saveAccessTokenData(accessTokenData);
      }
    }

    return accessTokenData;
  } catch (error) {
    console.error('Error retrieving access token data:', error);
    return undefined;
  }
}

function getFromLocalStorage(key) {
  const storedData = localStorage.getItem(key);
  return storedData ? JSON.parse(storedData) : undefined;
}

async function getFromPlatformStorage(key) {
  const storedData = await platform.getCredentials(key);
  return storedData ? JSON.parse(storedData) : undefined;
}

export function removeAccessTokenData() {
  platform.removeCredentials(ACCESS_TOKEN_DATA_KEY);
  localStorage.removeItem(ACCESS_TOKEN_DATA_KEY);
  localStorage.removeItem(SELECT_PROFILE);
  localStorage.removeItem(SELECT_PROFILES);
  accessTokenDataSubscription.notifySubscribers();
}

export async function getUserAccount() {
  const accessTokenData = await getAccessTokenData();
  return getUserAccountFromIdToken(accessTokenData?.idToken);
}

export function getUserAccountFromIdToken(idToken) {
  if (!idToken) return undefined;

  const decodedIdToken = jwtDecode(idToken);

  const {
    sub: username,
    businessUnit,
    givenName: firstName,
    iso2Code,
    familyName: lastName,
    vrioCustomerId: uid,
    name: fullName,
    channels,
    regions,
    customerStatus,
    msoProvider,
    customerId,
    iat,
    selectedSkySubscription,
    selfcareUrl,
  } = decodedIdToken;

  const accountType = getUserTypeByBusinessUnit(businessUnit);

  return {
    uid,
    username,
    firstName,
    lastName,
    fullName: fullName || `${firstName} ${lastName}`,
    accountType: accountType || 'OTT-D2C',
    iso2Code,
    businessUnit,
    channels,
    regions,
    customerStatus,
    msoProvider: msoProvider || 'Automation',
    customerId,
    iat,
    subscriberId: selectedSkySubscription,
    selfcareUrl,
  };
}

export function logout() {
  removeAccessTokenData();
  localStorage.removeItem('profiles');
  localStorage.removeItem('country');
}

export const ErrorCodes = {
  TOO_MUCH_TIME_AWAY: 'A10-{errorLocation}40',
  DEVICE_CONCURRENCY: 'A10-{errorLocation}21',
  DEVICE_LOGGED_OUT: 'A10-{errorLocation}25',
  DEVICE_TOKEN_EXPIRED: 'A10-{errorLocation}15',
  DEFAULT_LOGOUT_ERROR: 'A10-{errorLocation}16',
};

const GENERIC_ERROR_MESSAGE_ID = 'profile.error.auth.generic';

const ErrorMessages = {
  [ErrorCodes.TOO_MUCH_TIME_AWAY]: GENERIC_ERROR_MESSAGE_ID,
  [ErrorCodes.DEVICE_CONCURRENCY]: 'profile.error.auth.concurrency',
  [ErrorCodes.DEVICE_LOGGED_OUT]: GENERIC_ERROR_MESSAGE_ID,
  [ErrorCodes.DEVICE_TOKEN_EXPIRED]: GENERIC_ERROR_MESSAGE_ID,
  [ErrorCodes.DEFAULT_LOGOUT_ERROR]: GENERIC_ERROR_MESSAGE_ID,
};

// eslint-disable-next-line sonarjs/cognitive-complexity -- Easier to read this way
export const getErrorLocation = (requestError) => {
  const requestRoute = requestError?.config?.url;
  const currentRoute = window.location.pathname;

  if (requestRoute) {
    if (requestRoute.includes('epg')) return LOCATIONS_ERROR.EPG;
    if (requestRoute.includes('search')) return LOCATIONS_ERROR.SEARCH;
    if (requestRoute.includes('morelikethis')) return LOCATIONS_ERROR.SEE_MORE_LANDER;
    if (
      requestRoute.includes('playback/token/authorizer')
      || requestRoute.includes('/assets/')
    ) return LOCATIONS_ERROR.PLAYER_BEFORE_VIDEO_START;
  }

  if (currentRoute.includes('player')) return LOCATIONS_ERROR.PLAYER_PLAYBACK;
  if (currentRoute.includes('home')) return LOCATIONS_ERROR.HOME_PAGE;
  if (currentRoute.includes('search')) return LOCATIONS_ERROR.SEARCH;
  if (currentRoute.includes('detail')) return LOCATIONS_ERROR.PDP;
  if (currentRoute.includes('lander')) return LOCATIONS_ERROR.LANDER;
  if (currentRoute.includes('live')) return LOCATIONS_ERROR.LIVE_PAGE;
  if (currentRoute.includes('sports')) return LOCATIONS_ERROR.SPORTS_PAGE;
  if (currentRoute.includes('kids')) return LOCATIONS_ERROR.KIDS_PAGE;
  if (currentRoute.includes('setting')) return LOCATIONS_ERROR.SETTINGS;
  if (currentRoute.includes('lander')) return LOCATIONS_ERROR.LANDER;
  if (currentRoute.includes('my-list')) return LOCATIONS_ERROR.MY_LIST;
  if (currentRoute.includes('profile/create')) return LOCATIONS_ERROR.PROFILE_CREATE;
  if (currentRoute.includes('profile/edit')) return LOCATIONS_ERROR.PROFILE_EDIT;
  if (currentRoute.includes('profile/delete')) return LOCATIONS_ERROR.PROFILE_DELETE;
  if (
    currentRoute.includes('on-demand')
    || currentRoute.includes('collection')
  ) return LOCATIONS_ERROR.CATALOG_PAGE;

  return LOCATIONS_ERROR.BACKGROUND_SERVICE;
};

const getErrorDetails = (error) => {
  const errorLocation = getErrorLocation(error?.requestError);
  const resolveErrorCode = (template) => template.replace('{errorLocation}', errorLocation);

  const { code, details } = error?.axiosError?.response?.data || {};
  const { status } = error?.axiosError?.response || {};
  let errorCode = ErrorCodes.DEFAULT_LOGOUT_ERROR;

  if (code === ErrorCodes.TOO_MUCH_TIME_AWAY) {
    errorCode = ErrorCodes.TOO_MUCH_TIME_AWAY;
  } else if (status === 401) {
    if (details === 'TOKEN_ALREADY_LOGGED_OUT') {
      errorCode = ErrorCodes.DEVICE_LOGGED_OUT;
    } else if (code === 'DTV-007') {
      errorCode = ErrorCodes.DEVICE_CONCURRENCY;
    }
  }

  if (details === 'TOKEN_EXPIRED') {
    errorCode = ErrorCodes.DEVICE_TOKEN_EXPIRED;
  }

  const errorMessage = ErrorMessages[errorCode];

  let errorCodeWithDetails = resolveErrorCode(errorCode);
  if (code && errorCode !== ErrorCodes.TOO_MUCH_TIME_AWAY) {
    errorCodeWithDetails = `${resolveErrorCode(errorCode)} (${code})`;
  } else if (status && errorCode !== ErrorCodes.TOO_MUCH_TIME_AWAY) {
    errorCodeWithDetails = `${resolveErrorCode(errorCode)} (${status})`;
  }

  return { errorMessage, errorCodeWithDetails };
};

export function onSessionError(error) {
  const { errorMessage, errorCodeWithDetails } = getErrorDetails(error);

  logout();

  const urlToRedirect = getLink(Routes.ErrorPage, {
    query: {
      error: 'sessionError',
      errorCode: errorCodeWithDetails,
      errorMessage,
    },
  });

  redirectTo(urlToRedirect);
}
